import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { HR_CATEGORIES, HR_CATEGORY_ORDER, HR_ROLES, HR_STATUS, rolesForCategory, periodHint, openDocument } from "@/lib/hr";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Eye, Download, Pencil, Trash2, Pencil as EditIcon } from "lucide-react";

export default function HREmployeeDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [emp, setEmp] = useState(null);
  const [cat, setCat] = useState("salary");
  const [bundles, setBundles] = useState([]);
  const [editEmp, setEditEmp] = useState(null);
  const [editDoc, setEditDoc] = useState(null);

  const loadEmp = () => api.get(`/hrdoc/employees/${id}`).then((r) => setEmp(r.data));
  const loadBundles = () => api.get(`/hrdoc/employees/${id}/bundles`, { params: { category: cat } }).then((r) => setBundles(r.data.items));

  useEffect(() => { loadEmp(); }, [id]);
  useEffect(() => { loadBundles(); /* eslint-disable-next-line */ }, [id, cat]);

  const saveEmp = async () => {
    try {
      await api.put(`/hrdoc/employees/${id}`, editEmp);
      toast.success("Werknemer bijgewerkt");
      setEditEmp(null);
      loadEmp();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveDoc = async () => {
    try {
      await api.put(`/hrdoc/documents/${editDoc.id}`, editDoc);
      toast.success("Document bijgewerkt");
      setEditDoc(null);
      loadBundles();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (docId) => {
    if (!window.confirm("Dit document naar de prullenbak verplaatsen?")) return;
    try {
      await api.delete(`/hrdoc/documents/${docId}`);
      toast.success("Verplaatst naar prullenbak");
      loadBundles();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!emp) return <div className="text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-6" data-testid="hr-dossier">
      <Button variant="ghost" size="sm" onClick={() => navigate("/hr")} data-testid="hr-dossier-back">
        <ArrowLeft className="h-4 w-4 mr-2" /> Alle werknemers
      </Button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold">{emp.name}</h1>
          <p className="text-muted-foreground mt-1">{emp.national_id || "Geen rijksregisternummer"}{emp.start_date ? ` · in dienst ${emp.start_date}` : ""}{emp.end_date ? ` · uit dienst ${emp.end_date}` : ""}</p>
        </div>
        {canWrite && (
          <Button variant="outline" data-testid="hr-edit-emp-btn" onClick={() => setEditEmp({ name: emp.name, national_id: emp.national_id || "", start_date: emp.start_date || "", end_date: emp.end_date || "", active: emp.active !== false })}>
            <EditIcon className="h-4 w-4 mr-2" /> Bewerken
          </Button>
        )}
      </div>

      <Tabs value={cat} onValueChange={setCat}>
        <TabsList className="flex-wrap h-auto">
          {HR_CATEGORY_ORDER.map((c) => (
            <TabsTrigger key={c} value={c} data-testid={`hr-cat-${c}`}>{HR_CATEGORIES[c]}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        {bundles.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">Geen documenten in deze categorie.</CardContent></Card>}
        {bundles.map((b) => {
          const st = HR_STATUS[b.status] || HR_STATUS.problem;
          return (
            <Card key={b.id} data-testid={`hr-bundle-${b.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-lg">{b.period_key || "Onbekende periode"}</div>
                  <Badge className={st.cls}>{st.label}</Badge>
                </div>
                <div className="divide-y">
                  {b.documents.map((d) => (
                    <div key={d.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`hr-doc-${d.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{HR_ROLES[d.document_role]} · {d.name}</div>
                        <div className="text-xs text-muted-foreground">{d.document_type}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="icon" variant="ghost" title="Bekijken" data-testid={`hr-view-${d.id}`} onClick={() => openDocument(d.id)}><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title="Downloaden" onClick={() => openDocument(d.id, true)}><Download className="h-4 w-4" /></Button>
                        {canWrite && <Button size="icon" variant="ghost" title="Bewerken" data-testid={`hr-editdoc-${d.id}`} onClick={() => setEditDoc({ ...d })}><Pencil className="h-4 w-4" /></Button>}
                        {canWrite && <Button size="icon" variant="ghost" title="Verwijderen" data-testid={`hr-del-${d.id}`} onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit employee */}
      <Dialog open={!!editEmp} onOpenChange={(o) => !o && setEditEmp(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Werknemer bewerken</DialogTitle></DialogHeader>
          {editEmp && (
            <div className="space-y-3">
              <div><Label>Naam</Label><Input data-testid="edit-emp-name" value={editEmp.name} onChange={(e) => setEditEmp({ ...editEmp, name: e.target.value })} /></div>
              <div><Label>Rijksregisternummer</Label><Input value={editEmp.national_id} onChange={(e) => setEditEmp({ ...editEmp, national_id: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>In dienst</Label><Input type="date" value={editEmp.start_date} onChange={(e) => setEditEmp({ ...editEmp, start_date: e.target.value })} /></div>
                <div><Label>Uit dienst</Label><Input type="date" value={editEmp.end_date} onChange={(e) => setEditEmp({ ...editEmp, end_date: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmp(null)}>Annuleren</Button>
            <Button data-testid="edit-emp-save" onClick={saveEmp} className="bg-teal-600 hover:bg-teal-700">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit document metadata */}
      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Document bewerken</DialogTitle></DialogHeader>
          {editDoc && (
            <div className="space-y-3">
              <div><Label>Categorie</Label>
                <Select value={editDoc.category} onValueChange={(v) => { const roles = rolesForCategory(v); setEditDoc({ ...editDoc, category: v, document_role: roles.includes(editDoc.document_role) ? editDoc.document_role : roles[0], document_type: `${v}_${roles.includes(editDoc.document_role) ? editDoc.document_role : roles[0]}` }); }}>
                  <SelectTrigger data-testid="editdoc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{HR_CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{HR_CATEGORIES[c]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Type</Label>
                <Select value={editDoc.document_role} onValueChange={(v) => setEditDoc({ ...editDoc, document_role: v, document_type: `${editDoc.category}_${v}` })}>
                  <SelectTrigger data-testid="editdoc-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{rolesForCategory(editDoc.category).map((r) => <SelectItem key={r} value={r}>{HR_ROLES[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Periode</Label><Input data-testid="editdoc-period" value={editDoc.period_key} onChange={(e) => setEditDoc({ ...editDoc, period_key: e.target.value })} placeholder={periodHint(editDoc.category)} /></div>
              <div><Label>Naam</Label><Input value={editDoc.name} onChange={(e) => setEditDoc({ ...editDoc, name: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDoc(null)}>Annuleren</Button>
            <Button data-testid="editdoc-save" onClick={saveDoc} className="bg-teal-600 hover:bg-teal-700">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
